import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#problem-solution', label: 'The Problem' },
    { href: '#vibe', label: 'V.I.B.E.' },
    { href: '#about', label: 'The Solution' },
    { href: '#services', label: 'Services' },
    { href: '#pricing', label: 'Our Process' },
    { href: '#testimonials', label: 'Results' },
    { href: '#faq', label: 'FAQ' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-obsidian border-b border-white/5' : 'bg-obsidian'
        }`}
        style={{ backgroundColor: '#050505' }}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Mobile Menu Button - Left on mobile */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2 z-50"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>

            {/* Spacer for left side balance on desktop */}
            <div className="hidden lg:block w-[120px]"></div>

            {/* Desktop Navigation - Truly Centered */}
            <div className="hidden lg:flex items-center justify-center gap-10">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="font-heading text-lg uppercase tracking-wider text-white/70 hover:text-neon-blue transition-colors duration-300"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Let's Build Button - Right Side */}
            <a
              href="https://kingdomvibestudios.hbportal.co/public/69a9c1506b994e0034214eb6"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-neon btn-neon-gold text-sm px-4 sm:px-6 py-2 sm:py-3"
              data-testid="nav-book-btn"
            >
              <span>Let's Build</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden bg-obsidian"
            style={{ backgroundColor: '#050505' }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center min-h-screen gap-6 py-20">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="font-heading text-3xl uppercase tracking-wider text-white hover:text-neon-blue transition-colors"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href="https://kingdomvibestudios.hbportal.co/public/69a9c1506b994e0034214eb6"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="btn-neon btn-neon-gold mt-6"
                data-testid="mobile-book-btn"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span>Let's Build</span>
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
